import React, {useEffect, useState} from 'react';
import ReactSelect from 'react-select/async';
import ReactDatepicker from 'react-datepicker';

import {Alert} from '../components/Alert';
import {LoadingIndicator} from '../components/LoadingIndicator';
import {parseJSON} from '../_helpers/fetch';
import {Etl} from '../_services/Etl';
import {User} from '../_services/User';
import {ActivityButton} from '../components/ActivityButton';

const debounce = require('debounce-promise');

/**
 * NOTE: this does not belong here.. it should be it's own standalone app
 */

export const PaOnboardingManagerReview = () => {
    const [errorMessage, setErrorMessage] = useState('');
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [employees, setEmployees] = useState<any>();
    const [formState, setFormState] = useState<any>({});
    const [employeeState, setEmployeeState] = useState<any>({});
    const requestHeaders: any = {
        'Accept': 'application/json',
        'Content-Type': 'application/json'
    };
    const baseUrl = '/p/flow';

    // get employees needing completing by current user
    useEffect(() => {
        setIsLoading(true);

        fetch(baseUrl + '/api/paonboarding/employees', {
            headers: {
                ...requestHeaders,
                'Authorization': 'Bearer ' + User.getInstance().getAuthToken()
            },
            method: 'GET',
        })
            .then(parseJSON)
            .then(response => {
                if (response.ok) {
                    setEmployees(response?.json?.entries);

                    // initialize formstate
                    console.log('setting state', response?.json?.entries?.reduce((acc: any, entry: any) => {
                        return {
                            ...acc,
                            [entry.id]: {
                                startDate: '',
                                buddy: '',
                                buddyStartDate: '',
                                buddyEndDate: ''
                            }
                        }
                    }, {}));
                    setFormState(response?.json?.entries?.reduce((acc: any, entry: any) => {
                        return {
                            ...acc,
                            [entry.id]: {
                                startDate: '',
                                buddy: '',
                                buddyStartDate: '',
                                buddyEndDate: ''
                            }
                        }
                    }, {}));

                } else {
                    throw Error('');
                }
            })
            .catch((e) => {
                setErrorMessage(e?.message || e || 'Unrecoverable error. Please refresh the page and try again. If the error persists, open a support ticket using the menu above');
            })
            .finally(() => {
                setIsLoading(false);
            })
    }, []);

    const dateFromString = (dateString: string): Date | null => {
        if (dateString && dateString.match(/^\d{4}-\d{2}-\d{2}$/)) {
            const parts: string[] = dateString.split('-');
            // @ts-ignore
            return new Date(parts[0], parts[1] - 1, parts[2]);
        }
        return null;
    };

    const formatDate = (date: Date): string => {
        let month = '' + (date.getMonth() + 1);
        let day = '' + date.getDate();
        const year = date.getFullYear();

        if (month.length < 2)
            month = '0' + month;
        if (day.length < 2)
            day = '0' + day;

        return [year, month, day].join('-');
    };

    const handleEmailLookup = debounce(
        Etl.getInstance().employeeAutocompleter,
        250
    );

    const isSaving = (entryId: any) => {
        return (
            employeeState.hasOwnProperty(entryId) &&
            employeeState[entryId].hasOwnProperty('isSaving') &&
            employeeState[entryId].isSaving === true
        );
    }

    const handleSave = (entryId: any) => {

        if (!formState.hasOwnProperty(entryId)) {
            return;
        }

        if (!(formState[entryId].hasOwnProperty('buddy') && formState[entryId].buddy)) {
            setEmployeeState(state => ({
                ...state,
                [entryId]: {
                    isSaving: false,
                    saved: false,
                    error: true,
                    errorMessage: 'You must set a Buddy'
                }
            }));
            return
        }

        if (!(formState[entryId].hasOwnProperty('startDate') && formState[entryId].startDate)) {
            setEmployeeState(state => ({
                ...state,
                [entryId]: {
                    isSaving: false,
                    saved: false,
                    error: true,
                    errorMessage: 'You must set a Start Date'
                }
            }));
            return
        }

        setEmployeeState(state => ({
            ...state,
            [entryId]: {
                isSaving: true,
                saved: false,
                error: false,
                errorMessage: ''
            }
        }));

        fetch(baseUrl + '/api/paonboarding/employee', {
            headers: {
                ...requestHeaders,
                'Authorization': 'Bearer ' + User.getInstance().getAuthToken()
            },
            method: 'POST',
            body: JSON.stringify({
                id: entryId,
                ...formState[entryId]
            })
        })
            .then(parseJSON)
            .then(response => {
                if (response.ok) {
                    setEmployeeState(state => ({
                        ...state,
                        [entryId]: {
                            ...state[entryId],
                            saved: true,
                            error: false,
                            errorMessage: ''
                        }
                    }));
                } else {
                    setEmployeeState(state => ({
                        ...state,
                        [entryId]: {
                            ...state[entryId],
                            saved: false,
                            error: true,
                            errorMessage: response?.json?.message || 'Error saving. Please try again.'
                        }
                    }));
                }
            })
            .catch(e => {
                setEmployeeState(state => ({
                    ...state,
                    [entryId]: {
                        ...state[entryId],
                        saved: false,
                        error: true,
                        errorMessage: e?.message || 'Error saving. Please try again.'
                    }
                }));
            })
            .finally(() => {
                setEmployeeState(state => ({
                    ...state,
                    [entryId]: {
                        ...state[entryId],
                        isSaving: false
                    }
                }));
            });
    }

    return (
        <>
            {isLoading && <LoadingIndicator/>}
            <h1>PA Orientation Program</h1>
            {errorMessage && <Alert type={'danger'} message={errorMessage}/>}

            {employees && employees?.length === 0 &&
                <p><em>You have no new joiners to review</em></p>
            }

            {employees && employees?.length > 0 &&
                <>
                    <p>Complete the fields below for your new joiner(s) and click the <em>Submit</em> button</p>
                    <table className="table table-sm table-bordered">
                        <thead>
                        <tr>
                            <th>Employee</th>
                            {/*
                            <th>Manager</th>
                            <th>HRBP</th>
                            */}
                            <th>Start Date</th>
                            <th style={{width: '300px'}}>Buddy</th>
                            {/*
                            <th>Buddy Start Date</th>
                            <th>Buddy End Date</th>
                            */}
                            <th></th>
                        </tr>
                        </thead>
                        <tbody>
                        {employees.map(e => (
                            <tr>
                                <td style={{minWidth: '300px'}}>
                                    <div>{e['3']} {e['4']}</div>
                                    <div>{e['5']}</div>
                                </td>
                                {/*
                                <td>{e['6']}</td>
                                <td>{e['7']}</td>
                                */}
                                <td>
                                    <ReactDatepicker
                                        id={`start_date_${e.id}`}
                                        required={true}
                                        className={'form-control'}
                                        dateFormat={'MM/dd/yyyy'}
                                        onChange={(dateObj: Date) => {
                                            setFormState(state => ({
                                                ...state,
                                                [e.id]: {
                                                    ...state[e.id],
                                                    startDate: formatDate(dateObj)
                                                }
                                            }))
                                        }}
                                        selected={dateFromString(formState[e.id]?.startDate || '')}
                                    />
                                </td>
                                <td>
                                    <ReactSelect
                                        isMulti={false}
                                        value={formState[e.id]?.buddy || null}
                                        onChange={(option: any) => {
                                            setFormState(state => ({
                                                ...state,
                                                [e.id]: {
                                                    ...state[e.id],
                                                    buddy: option
                                                }
                                            }))
                                        }}
                                        placeholder="Enter an email address"
                                        loadOptions={handleEmailLookup}
                                    />
                                </td>
                                {/*}
                                <td>
                                    <ReactDatepicker
                                        id={`buddy_start_date_${e.id}`}
                                        required={true}
                                        className={'form-control'}
                                        dateFormat={'MM/dd/yyyy'}
                                        onChange={(dateObj: Date) => {
                                            setFormState(state => ({
                                                ...state,
                                                [e.id]: {
                                                    ...state[e.id],
                                                    buddyStartDate: formatDate(dateObj)
                                                }
                                            }))
                                        }}
                                        selected={dateFromString(formState[e.id]?.buddyStartDate || '')}
                                    />
                                </td>
                                <td>
                                    <ReactDatepicker
                                        id={`buddy_end_date_${e.id}`}
                                        required={true}
                                        className={'form-control'}
                                        dateFormat={'MM/dd/yyyy'}
                                        onChange={(dateObj: Date) => {
                                            setFormState(state => ({
                                                ...state,
                                                [e.id]: {
                                                    ...state[e.id],
                                                    buddyEndDate: formatDate(dateObj)
                                                }
                                            }))
                                        }}
                                        selected={dateFromString(formState[e.id]?.buddyEndDate || '')}
                                    />
                                </td>
                                */}
                                <td>
                                    {
                                        (employeeState.hasOwnProperty(e.id) &&
                                            employeeState[e.id].hasOwnProperty('saved') &&
                                            employeeState[e.id].saved) ?
                                            <span className="text-success">Saved</span>
                                            :
                                            <>
                                                <ActivityButton type="button"
                                                                isActive={isSaving(e.id)}
                                                                className="btn btn-sm btn-primary"
                                                                onClick={() => {
                                                                    handleSave(e.id);
                                                                }}
                                                >Submit
                                                </ActivityButton>
                                                {(employeeState.hasOwnProperty(e.id) &&
                                                        employeeState[e.id].hasOwnProperty('error') &&
                                                        employeeState[e.id].hasOwnProperty('errorMessage') &&
                                                        employeeState[e.id].error &&
                                                        employeeState[e.id].errorMessage) &&
                                                    <span className="d-block text-danger">{employeeState[e.id].errorMessage}</span>
                                                }
                                            </>
                                    }
                                </td>
                            </tr>
                        ))}
                        </tbody>
                    </table>
                </>
            }
        </>
    );
}